import { makeAuthenticatedRequest } from './authService';
import { Book } from '../types/Book';
import { LessonSummary } from './lessonServiceTypes';

export interface ChapterWithNovel {
  chapter_id: string;
  title: string;
  block_id: string;
  chapter_created_at: string;
  novel_id: string;
  novel_title: string;
  novel_genre: string | null;
  novel_metadata: any;
}

export interface CuriosityContext {
  books: Book[];
  chapters: ChapterWithNovel[];
  lessons: LessonSummary[];
}

export interface CuriosityContent {
  id?: number;
  content: string;
  generatedAt: string;
}

export interface CuriosityGeneration {
  id: number;
  content: string;
  contentPreview?: string;
  contextSnapshot?: CuriosityContext | null;
  createdAt: string;
}

/**
 * Service for aggregating user's learning context and generating curiosity-driven content
 */
export class CuriosityService {
  /**
   * Aggregate user's learning context from books, novels, and knowledge lessons
   * Uses a single API endpoint to fetch all data efficiently
   */
  static async aggregateContext(userId: number): Promise<CuriosityContext> {
    try {
      // Use the new aggregated endpoint that fetches everything in one go
      const result = await makeAuthenticatedRequest('/curiosity/context', {
        method: 'GET'
      });

      if (!result.success || !result.data) {
        throw new Error(result.message || 'Failed to fetch context');
      }

      // Transform backend data to match frontend types
      const books: Book[] = result.data.books.map((book: any) => ({
        id: book.book_id,
        title: book.title,
        author: book.author || '',
        genre: '',
        status: 'currently_reading' as any,
        tags: book.subjects || [],
        currentPage: book.current_page,
        totalPages: book.total_pages,
        readingStatus: book.reading_status || 'reading',
        dateAdded: '',
        pageCount: book.total_pages
      }));

      const chapters: ChapterWithNovel[] = result.data.chapters.map((chapter: any) => ({
        chapter_id: chapter.chapter_id,
        title: chapter.title,
        block_id: chapter.block_id,
        chapter_created_at: chapter.chapter_created_at,
        novel_id: chapter.novel_id,
        novel_title: chapter.novel_title,
        novel_genre: chapter.novel_genre,
        novel_metadata: chapter.novel_metadata
      }));

      const lessons: LessonSummary[] = result.data.lessons.map((lesson: any) => ({
        lesson_id: lesson.lesson_id,
        lesson_name: lesson.lesson_name,
        lesson_objective: lesson.lesson_objective,
        category: lesson.category,
        created_at: lesson.created_at,
        updated_at: lesson.updated_at
      }));

      return {
        books: books,
        chapters: chapters,
        lessons: lessons
      };
    } catch (error) {
      console.error('Error aggregating curiosity context:', error);
      throw error;
    }
  }

  /**
   * Build context string from aggregated data for AI prompt
   */
  static buildContextString(context: CuriosityContext): string {
    let contextString = 'USER\'S CURRENT LEARNING CONTEXT:\n\n';

    // Books section
    if (context.books.length > 0) {
      contextString += 'BOOKS CURRENTLY BEING READ:\n';
      context.books.forEach((book, index) => {
        contextString += `${index + 1}. "${book.title}" by ${book.author}`;
        if (book.tags && book.tags.length > 0) {
          contextString += ` (Topics: ${book.tags.join(', ')})`;
        }
        if (book.currentPage && book.totalPages) {
          contextString += ` - Progress: ${book.currentPage}/${book.totalPages} pages`;
        }
        contextString += '\n';
      });
      contextString += '\n';
    } else {
      contextString += 'BOOKS: None currently being read\n\n';
    }

    // Chapters section (AI-written chapters from past 7 days)
    if (context.chapters.length > 0) {
      contextString += 'RECENT CHAPTERS WRITTEN (Past 7 Days):\n';
      // Group chapters by novel
      const chaptersByNovel: { [novelId: string]: ChapterWithNovel[] } = {};
      context.chapters.forEach(chapter => {
        if (!chaptersByNovel[chapter.novel_id]) {
          chaptersByNovel[chapter.novel_id] = [];
        }
        chaptersByNovel[chapter.novel_id].push(chapter);
      });

      Object.keys(chaptersByNovel).forEach((novelId, novelIndex) => {
        const novelChapters = chaptersByNovel[novelId];
        const firstChapter = novelChapters[0];
        contextString += `\n${novelIndex + 1}. Novel: "${firstChapter.novel_title}"`;
        if (firstChapter.novel_genre) {
          contextString += ` (Genre: ${firstChapter.novel_genre})`;
        }
        if (firstChapter.novel_metadata?.oneParagraphSummary) {
          contextString += `\n   Summary: ${firstChapter.novel_metadata.oneParagraphSummary}`;
        }
        contextString += '\n   Chapters:\n';
        novelChapters.forEach((chapter, chIndex) => {
          contextString += `     ${chIndex + 1}. "${chapter.title}"\n`;
        });
      });
      contextString += '\n';
    } else {
      contextString += 'CHAPTERS: None written in the past 7 days\n\n';
    }

    // Knowledge lessons section
    if (context.lessons.length > 0) {
      contextString += 'RECENT KNOWLEDGE LESSONS LEARNED:\n';
      const lessonsByCategory: { [key: string]: LessonSummary[] } = {};
      context.lessons.forEach(lesson => {
        if (!lessonsByCategory[lesson.category]) {
          lessonsByCategory[lesson.category] = [];
        }
        lessonsByCategory[lesson.category].push(lesson);
      });

      Object.keys(lessonsByCategory).forEach(category => {
        contextString += `\n${category}:\n`;
        lessonsByCategory[category].forEach((lesson, index) => {
          contextString += `  ${index + 1}. ${lesson.lesson_name}: ${lesson.lesson_objective}\n`;
        });
      });
      contextString += '\n';
    } else {
      contextString += 'KNOWLEDGE LESSONS: None yet\n\n';
    }

    return contextString;
  }

  /**
   * Generate curiosity-driven learning content using AI
   */
  static async generateCuriosityContent(
    context: CuriosityContext
  ): Promise<CuriosityContent> {
    try {
      const contextString = this.buildContextString(context);

      const systemPrompt = `You are a curiosity-driven learning assistant. Your role is to generate novel, out-of-the-box learning suggestions and curiosity-driven content that helps users extend their knowledge in unexpected and interesting directions.

Based on the user's current learning context (books they're reading, novels they've written, knowledge lessons they've studied), generate something NEW and INTERESTING that:
1. Connects ideas across different domains they're exploring
2. Suggests unexpected connections or interdisciplinary approaches
3. Proposes novel questions or research directions
4. Introduces adjacent topics they might not have considered
5. Challenges assumptions or offers alternative perspectives

Be creative, thought-provoking, and genuinely helpful. The goal is to spark curiosity and inspire new learning paths.`;

      const userMessage = `${contextString}

Based on this learning context, generate something novel and out-of-the-box that would extend my knowledge in an interesting direction. This could be:
- An unexpected connection between topics I'm exploring
- A new research question or direction
- An adjacent topic I might not have considered
- A different perspective or approach to something I'm learning
- An interdisciplinary idea that bridges my interests

Be creative and thought-provoking. Generate content that genuinely sparks curiosity and opens up new learning possibilities.`;

      const result = await makeAuthenticatedRequest('/ai/chat', {
        method: 'POST',
        body: JSON.stringify({
          provider: 'claude',
          model: 'claude-haiku-4-5-20251001',
          systemPrompt: systemPrompt,
          messages: [
            {
              role: 'user',
              content: userMessage
            }
          ],
          maxTokens: 4000,
          temperature: 0.9
        })
      });

      // Extract AI response
      let aiResponse: string = '';

      // Check result.data.content first (wrapped response)
      if (result.data?.content && Array.isArray(result.data.content) && result.data.content.length > 0) {
        const textContent = result.data.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }

      // Check result.data.response
      if (!aiResponse && result.data?.response && typeof result.data.response === 'string') {
        aiResponse = result.data.response;
      }

      // Check result.content array
      if (!aiResponse && Array.isArray(result.content) && result.content.length > 0) {
        const textContent = result.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }

      // Check result.response
      if (!aiResponse && typeof result.response === 'string' && result.response) {
        aiResponse = result.response;
      }

      // Check result.content as string
      if (!aiResponse && typeof result.content === 'string' && result.content) {
        aiResponse = result.content;
      }

      if (!aiResponse) {
        throw new Error('AI returned empty response');
      }

      return {
        content: aiResponse,
        generatedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error generating curiosity content:', error);
      throw error;
    }
  }

  /**
   * Save a generated curiosity content to the database
   */
  static async saveGeneration(
    content: string,
    contextSnapshot?: CuriosityContext
  ): Promise<CuriosityGeneration> {
    try {
      const result = await makeAuthenticatedRequest('/curiosity/generations', {
        method: 'POST',
        body: JSON.stringify({
          content,
          contextSnapshot
        })
      });

      if (!result.success || !result.data) {
        throw new Error(result.message || 'Failed to save generation');
      }

      return {
        id: result.data.id,
        content: result.data.content,
        contextSnapshot: result.data.contextSnapshot,
        createdAt: result.data.createdAt
      };
    } catch (error) {
      console.error('Error saving generation:', error);
      throw error;
    }
  }

  /**
   * Get all generations for the current user
   */
  static async getGenerations(): Promise<CuriosityGeneration[]> {
    try {
      const result = await makeAuthenticatedRequest('/curiosity/generations', {
        method: 'GET'
      });

      if (!result.success || !result.data) {
        throw new Error(result.message || 'Failed to fetch generations');
      }

      return result.data.map((gen: any) => ({
        id: gen.id,
        content: gen.content,
        contentPreview: gen.contentPreview,
        contextSnapshot: gen.contextSnapshot,
        createdAt: gen.createdAt
      }));
    } catch (error) {
      console.error('Error fetching generations:', error);
      throw error;
    }
  }

  /**
   * Get a specific generation by ID
   */
  static async getGeneration(id: number): Promise<CuriosityGeneration> {
    try {
      const result = await makeAuthenticatedRequest(`/curiosity/generations/${id}`, {
        method: 'GET'
      });

      if (!result.success || !result.data) {
        throw new Error(result.message || 'Failed to fetch generation');
      }

      return {
        id: result.data.id,
        content: result.data.content,
        contextSnapshot: result.data.contextSnapshot,
        createdAt: result.data.createdAt
      };
    } catch (error) {
      console.error('Error fetching generation:', error);
      throw error;
    }
  }

  /**
   * Delete a generation by ID
   */
  static async deleteGeneration(id: number): Promise<void> {
    try {
      const result = await makeAuthenticatedRequest(`/curiosity/generations/${id}`, {
        method: 'DELETE'
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete generation');
      }
    } catch (error) {
      console.error('Error deleting generation:', error);
      throw error;
    }
  }
}

